import {
  OpportunityWizardStep,
  type ConfidentialityLevel,
  type DealSize,
  type Opportunity,
  type OpportunityAccountSummary,
  type OpportunityBidTeamMember,
  type OpportunityFormData,
  type OpportunityPipelineStage,
  type ProcurementRoute,
  type WorkspaceFileProcessingStatusValue,
  type WorkspaceStoragePlacement,
  PROCUREMENT_ROUTE_VALUES,
} from './models/opportunity.models';
import { isBlank, nonEmptyTextOrNull } from '../../shared/string.util';
import { normalizeIsoCalendarDate } from '../../shared/utils/iso-calendar-date.util';
import { ensureMinRows } from '../../shared/utils/editable-row-list.util';
import type { IndustryDto } from '../../shared/industries/models/industry.models';
import type { CompanyDto } from '../../shared/companies/models/company.models';
import {
  catalogUserOId,
  isCatalogUser,
  isEmptyMomentumUser,
  isPartialMomentumUser,
  momentumUserSnapshotFromApi,
  type MomentumUser,
} from '../../shared/models/momentum-user.models';
import { decisionFromApi } from './opportunity-wizard/steps/opportunity-assessment/opportunity-assessment-api/opportunity-assessment.mapper';
import type { OpportunityAssessmentDecisionStatus } from './opportunity-wizard/steps/opportunity-assessment/opportunity-assessment-api/opportunity-assessment-api.models';
import type {
  OpportunityAccountPotential,
  OpportunityExistingRelationship,
} from './models/opportunity-commercial.models';

// ─── Wizard step (AppApi `OpportunityStep`, JsonStringEnumConverter) ─────────

export type OpportunityApiStep =
  | 'UploadAndStart'
  | 'SmartDiscovery'
  | 'Scotsman'
  | 'OpportunityMetadata'
  | 'OpportunityAssessment';

const API_STEP_RANK: Record<OpportunityApiStep, number> = {
  UploadAndStart: 0,
  SmartDiscovery: 1,
  Scotsman: 2,
  OpportunityMetadata: 3,
  OpportunityAssessment: 4,
};

export function apiStepFromWizardStep(step: OpportunityWizardStep): OpportunityApiStep {
  switch (step) {
    case OpportunityWizardStep.Step2SmartDiscovery:
      return 'SmartDiscovery';
    case OpportunityWizardStep.Step3Scotsman:
      return 'Scotsman';
    case OpportunityWizardStep.Step4OpportunityMetadata:
      return 'OpportunityMetadata';
    case OpportunityWizardStep.Step5OpportunityAssessment:
      return 'OpportunityAssessment';
    case OpportunityWizardStep.Step1UploadStart:
    default:
      return 'UploadAndStart';
  }
}

/** Maps API step string to UI wizard step; unknown/missing → Upload & Start. */
export function wizardStepFromApiStep(raw: string | null | undefined): OpportunityWizardStep {
  switch (raw) {
    case 'SmartDiscovery':
      return OpportunityWizardStep.Step2SmartDiscovery;
    case 'Scotsman':
      return OpportunityWizardStep.Step3Scotsman;
    case 'OpportunityMetadata':
      return OpportunityWizardStep.Step4OpportunityMetadata;
    case 'OpportunityAssessment':
      return OpportunityWizardStep.Step5OpportunityAssessment;
    case 'UploadAndStart':
    default:
      return OpportunityWizardStep.Step1UploadStart;
  }
}

export function furthestApiStep(
  current: OpportunityApiStep | undefined,
  requested: OpportunityApiStep,
): OpportunityApiStep {
  if (!current) {
    return requested;
  }
  return API_STEP_RANK[requested] > API_STEP_RANK[current] ? requested : current;
}

// ─── Wire DTOs (AppApi JSON; camelCase) ──────────────────────────────────────

/** Matches AppApi `OpportunityBidTeamMember` JSON. */
export interface OpportunityBidTeamMemberApiDto {
  user?: MomentumUser | null;
  roleLabel?: string | null;
}

/** Matches AppApi `OpportunityResponse` JSON. */
/** Matches AppApi nested `AccountSummary` on `OpportunityResponse`. */
export interface OpportunityAccountSummaryDto {
  id: string;
  name: string;
  industry?: IndustryDto | null;
  company?: CompanyDto | null;
}

export interface OpportunityResponseDto {
  id: string;
  workspaceId: string;
  accountId?: string | null;
  account?: OpportunityAccountSummaryDto | null;
  crmOpportunityId?: string | null;
  crmProjectId?: string | null;
  cNumber?: string | null;
  title: string;
  customerName: string;
  dealSize: string;
  submissionDeadline?: string | null;
  opportunityOwner?: MomentumUser | null;
  proposalManager?: MomentumUser | null;
  bidTeamMembers?: OpportunityBidTeamMemberApiDto[] | null;
  procurementRoute?: string | null;
  briefDescription?: string | null;
  miroWebUrl?: string | null;
  sharePointFolderName?: string | null;
  sharePointFolderWebUrl?: string | null;
  crmOpportunityWebUrl?: string | null;
  salesStage?: OpportunityApiSalesStage | string | null;
  step?: OpportunityApiStep | string | null;
  assessmentDecisionStatus?: OpportunityAssessmentDecisionStatus | string | null;
  isCompleted?: boolean;
  completedAt?: string | null;
  workspacePlacement?: WorkspaceStoragePlacement | string | null;
  isFrameContract?: boolean;
  currency?: string;
  tcv?: number | null;
  tcvLocal?: number | null;
  bidBudget?: number | null;
  accountPotential?: OpportunityApiAccountPotential | string | null;
  existingRelationship?: OpportunityApiExistingRelationship | string | null;
  createdAt: string;
  updatedAt: string;
}

export type OpportunityApiAccountPotential = 'Small' | 'Medium' | 'Large';

export type OpportunityApiExistingRelationship = 'None' | 'Partial' | 'Strong';

/** Matches AppApi `OpportunitySalesStage` (JsonStringEnumConverter). */
export type OpportunityApiSalesStage = 'Qualify' | 'Develop' | 'Closed';

export interface CreateWorkspaceWithOpportunityResponseDto {
  workspaceId: string;
  opportunityId: string;
  workspace: {
    folderName?: string;
    opportunity: OpportunityResponseDto;
  };
}

/** Workspace file row from GET/POST files. */
export interface WorkspaceFilePublicDto {
  id: string;
  /** One ingestion package per multipart upload batch; shared by all files in that batch. */
  opportunityIngestionPackageId?: string | null;
  correlationId?: string | null;
  workspaceId: string;
  fileName: string;
  contentType: string;
  uploadedAt: string;
  uploadedBy: string;
  processingStatus: string;
  lastProcessingError?: string | null;
  indexStatus?: string | null;
  sizeInBytes?: number | null;
  sharePointWebUrl?: string | null;
}

/** AppApi GET `.../files/ingestion-status` (camelCase; enum values as strings). */
export interface WorkspaceFileIngestionStatusItemDto {
  id: string;
  statusCode: string;
}

export interface WorkspaceFilesIngestionStatusResponseDto {
  overallStatus: string;
  totalFiles: number;
  completedCount: number;
  inProgressCount: number;
  failedCount: number;
  files: WorkspaceFileIngestionStatusItemDto[];
}

const WORKSPACE_FILE_PROCESSING_STATUS_VALUES: readonly WorkspaceFileProcessingStatusValue[] = [
  'pending',
  'processed',
  'failed',
  'chunked',
  'indexed',
];

/** Maps AppApi `statusCode` (PascalCase or lower-case string) to UI model (lower-cased). */
export function workspaceFileProcessingStatusFromIngestionStatusCode(
  raw: unknown,
): WorkspaceFileProcessingStatusValue | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const normalized = raw.trim().toLowerCase() as WorkspaceFileProcessingStatusValue;
  return WORKSPACE_FILE_PROCESSING_STATUS_VALUES.includes(normalized) ? normalized : undefined;
}

/** POST /opportunities/workspaces request body. */
export interface CreateWorkspaceRequestBody {
  title?: string;
  accountId?: string;
  crmOpportunityId?: string;
  customerName?: string;
  dealSize?: string;
  submissionDeadline?: string;
  procurementRoute?: ProcurementRoute;
  briefDescription?: string;
  confidentiality?: string;
}

// ─── Date / deadline helpers ─────────────────────────────────────────────────

export function parseSubmissionDeadlineForApi(value: string | undefined): string | undefined {
  if (isBlank(value)) return undefined;
  const trimmed = value.trim();
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(trimmed);
  if (m) {
    const d = Number(m[1]);
    const mo = Number(m[2]);
    const y = Number(m[3]);
    const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
    return dt.toISOString();
  }
  const t = Date.parse(trimmed);
  if (!Number.isNaN(t)) return new Date(t).toISOString();
  return undefined;
}

/** Maps API/display deadline strings to `YYYY-MM-DD` for date pickers. */
export function formatSubmissionDeadlineToDateInputValue(value: string | undefined): string {
  const iso = parseSubmissionDeadlineForApi(value);
  return iso ? normalizeIsoCalendarDate(iso) : '';
}

export function formatIsoDateToDisplay(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

/** Local `dd.mm.yyyy HH:mm` for opportunity created/updated timestamps in lists. */
export function formatIsoDateTimeToDisplay(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()} ${hours}:${minutes}`;
}

// ─── UI ↔ API mapping ────────────────────────────────────────────────────────

const DEAL_SIZE_VALUES = new Set<string>(['S', 'M', 'L', 'XL', 'NV']);
const PROCUREMENT_ROUTE_VALUES_SET = new Set<string>(PROCUREMENT_ROUTE_VALUES);

/** Maps AppApi `procurementRoute` wire enum to UI model; unknown / `ALL` → unset. */
export function procurementRouteFromOpportunityApi(
  raw: string | null | undefined,
): ProcurementRoute | undefined {
  if (!raw || raw === 'ALL') {
    return undefined;
  }
  return PROCUREMENT_ROUTE_VALUES_SET.has(raw) ? (raw as ProcurementRoute) : undefined;
}

/** Maps API values to UI `DealSize`; unknown or unset → `''`. */
export function parseDealSize(raw: string | null | undefined): DealSize {
  const v = (raw ?? '').trim();
  if (!v) return '';
  const upper = v.toUpperCase();
  if (upper === 'UNSPECIFIED') return '';
  if (upper === 'NV') return 'NV';
  return DEAL_SIZE_VALUES.has(upper) ? (upper as DealSize) : '';
}

/** Maps UI `DealSize` to AppApi enum string; omit only when field not in patch. */
export function mapDealSizeToApi(dealSize: DealSize | undefined): string | undefined {
  if (dealSize === undefined) return undefined;
  if (dealSize === '') return 'Unspecified';
  if (dealSize === 'NV') return 'Nv';
  return dealSize;
}

export function accountPotentialFromOpportunityApi(
  raw: string | null | undefined,
): OpportunityAccountPotential {
  switch (raw) {
    case 'Small':
      return 'S';
    case 'Medium':
      return 'M';
    case 'Large':
      return 'L';
    default:
      return '';
  }
}

export function accountPotentialToOpportunityApi(
  value: OpportunityAccountPotential | undefined,
): OpportunityApiAccountPotential | undefined {
  if (value === 'S') return 'Small';
  if (value === 'M') return 'Medium';
  if (value === 'L') return 'Large';
  return undefined;
}

export function existingRelationshipFromOpportunityApi(
  raw: string | null | undefined,
): OpportunityExistingRelationship {
  switch (raw) {
    case 'None':
      return 'none';
    case 'Partial':
      return 'partial';
    case 'Strong':
      return 'strong';
    default:
      return '';
  }
}

export function existingRelationshipToOpportunityApi(
  value: OpportunityExistingRelationship | undefined,
): OpportunityApiExistingRelationship | undefined {
  if (value === 'none') return 'None';
  if (value === 'partial') return 'Partial';
  if (value === 'strong') return 'Strong';
  return undefined;
}

/** Maps AppApi `salesStage` to UI pipeline stage; unknown or missing → qualify. */
export function salesStageFromApi(
  raw: OpportunityApiSalesStage | string | null | undefined,
): OpportunityPipelineStage {
  switch (raw) {
    case 'Develop':
      return 'develop';
    case 'Closed':
      return 'closed';
    case 'Qualify':
    default:
      return 'qualify';
  }
}

export function workspacePlacementFromApi(
  raw: WorkspaceStoragePlacement | string | null | undefined,
): WorkspaceStoragePlacement | undefined {
  switch (raw) {
    case 'Blob':
    case 'Qualification':
    case 'Bid':
    case 'LegacyProposals':
      return raw;
    default:
      return undefined;
  }
}

export function bidTeamMembersFromApi(
  members: OpportunityBidTeamMemberApiDto[] | null | undefined,
): OpportunityBidTeamMember[] {
  return (members ?? []).map((member) => ({
    user: momentumUserSnapshotFromApi(member.user),
    role: member.roleLabel ?? '',
  }));
}

export function emptyBidTeamMemberRow(): OpportunityBidTeamMember {
  return { user: null, role: '' };
}

export function ensureBidTeamMemberRows(
  members: OpportunityBidTeamMember[] | null | undefined,
): OpportunityBidTeamMember[] {
  return ensureMinRows(members, emptyBidTeamMemberRow);
}

export function isEmptyBidTeamMemberRow(member: OpportunityBidTeamMember): boolean {
  return isEmptyMomentumUser(member.user) && isBlank(member.role);
}

export function isBidTeamMemberRowMissingRole(member: OpportunityBidTeamMember): boolean {
  if (isEmptyBidTeamMemberRow(member)) {
    return false;
  }

  return isBlank(member.role);
}

export function areBidTeamMembersValid(
  members: OpportunityBidTeamMember[] | null | undefined,
): boolean {
  return !(members ?? []).some(isBidTeamMemberRowMissingRole);
}

export function isPersistableBidTeamMemberRow(member: OpportunityBidTeamMember): boolean {
  if (isEmptyBidTeamMemberRow(member)) {
    return false;
  }

  if (isBlank(member.role)) {
    return false;
  }

  if (isPartialMomentumUser(member.user)) {
    return false;
  }

  return isEmptyMomentumUser(member.user) || isCatalogUser(member.user);
}

export function persistedBidTeamMembers(
  members: OpportunityBidTeamMember[] | null | undefined,
): OpportunityBidTeamMember[] {
  return (members ?? []).filter((member) => !isEmptyBidTeamMemberRow(member));
}

export function isBidTeamMemberRowRoleWithoutUser(member: OpportunityBidTeamMember): boolean {
  if (isEmptyBidTeamMemberRow(member)) {
    return false;
  }

  return isEmptyMomentumUser(member.user) && !isBlank(member.role);
}

export interface BidTeamOaGroupedMembers {
  unassigned: OpportunityBidTeamMember[];
  assigned: OpportunityBidTeamMember[];
}

export interface BidTeamOaLeadRoles {
  opportunityOwner?: MomentumUser | null;
  proposalManager?: MomentumUser | null;
}

const BID_TEAM_OA_OPPORTUNITY_OWNER_ROLE = 'Opportunity Owner';
const BID_TEAM_OA_PROPOSAL_MANAGER_ROLE = 'Proposal Manager';

function bidTeamLeadRoleRows(leadRoles?: BidTeamOaLeadRoles): OpportunityBidTeamMember[] {
  if (!leadRoles) {
    return [];
  }

  return [
    { role: BID_TEAM_OA_OPPORTUNITY_OWNER_ROLE, user: leadRoles.opportunityOwner ?? null },
    { role: BID_TEAM_OA_PROPOSAL_MANAGER_ROLE, user: leadRoles.proposalManager ?? null },
  ];
}

function bidTeamOaRows(
  members: OpportunityBidTeamMember[] | null | undefined,
  leadRoles?: BidTeamOaLeadRoles,
): OpportunityBidTeamMember[] {
  return [...bidTeamLeadRoleRows(leadRoles), ...persistedBidTeamMembers(members)];
}

function groupBidTeamOaRows(rows: OpportunityBidTeamMember[]): BidTeamOaGroupedMembers {
  return {
    unassigned: rows.filter(isBidTeamMemberRowRoleWithoutUser),
    assigned: rows.filter((member) => !isBidTeamMemberRowRoleWithoutUser(member)),
  };
}

export function bidTeamEmptyOaWarning(
  members: OpportunityBidTeamMember[] | null | undefined,
  leadRoles?: BidTeamOaLeadRoles,
): string | null {
  if (persistedBidTeamMembers(members).length > 0) {
    return null;
  }

  if (groupBidTeamOaRows(bidTeamOaRows(members, leadRoles)).assigned.length === 0) {
    return null;
  }

  return 'No additional bid team members were captured in metadata. Review the bid team before deciding.';
}

export function bidTeamMembersGroupedForOa(
  members: OpportunityBidTeamMember[] | null | undefined,
  leadRoles?: BidTeamOaLeadRoles,
): BidTeamOaGroupedMembers {
  return groupBidTeamOaRows(bidTeamOaRows(members, leadRoles));
}

export function bidTeamMembersToApiPatch(
  members: OpportunityBidTeamMember[],
): OpportunityBidTeamMemberPatchApiDto[] | undefined {
  const nonEmptyRows = members.filter((member) => !isEmptyBidTeamMemberRow(member));
  const persistableRows = nonEmptyRows.filter(isPersistableBidTeamMemberRow);

  if (nonEmptyRows.length > 0 && persistableRows.length === 0) {
    return undefined;
  }

  return persistableRows.map((member) => ({
    userOId: isCatalogUser(member.user) ? catalogUserOId(member.user) : null,
    roleLabel: member.role.trim(),
  }));
}

function mapAccountSummary(
  dto: OpportunityAccountSummaryDto | null | undefined,
): OpportunityAccountSummary | undefined {
  if (!dto) {
    return undefined;
  }

  return {
    id: dto.id,
    name: dto.name,
    industry: dto.industry ?? undefined,
    company: dto.company ?? undefined,
  };
}

function accountDerivedIndustry(
  account: OpportunityAccountSummary | undefined,
): IndustryDto | undefined {
  return account?.industry;
}

function accountDerivedCompany(
  account: OpportunityAccountSummary | undefined,
): CompanyDto | undefined {
  return account?.company;
}

export function mapOpportunityResponseDtoToOpportunity(dto: OpportunityResponseDto): Opportunity {
  const account = mapAccountSummary(dto.account);
  return {
    id: dto.id,
    workspaceId: dto.workspaceId,
    accountId: dto.accountId ?? undefined,
    account,
    crmOpportunityId: dto.crmOpportunityId ?? undefined,
    crmProjectId: dto.crmProjectId ?? undefined,
    title: dto.title,
    client: dto.customerName,
    cNumber: dto.cNumber ?? undefined,
    confidentiality: 'confidential',
    dealSize: parseDealSize(dto.dealSize),
    submissionDeadline: formatIsoDateToDisplay(dto.submissionDeadline ?? undefined),
    description: dto.briefDescription ?? undefined,
    opportunityOwner: momentumUserSnapshotFromApi(dto.opportunityOwner),
    proposalManager: momentumUserSnapshotFromApi(dto.proposalManager),
    bidTeamMembers: bidTeamMembersFromApi(dto.bidTeamMembers),
    industry: accountDerivedIndustry(account),
    company: accountDerivedCompany(account),
    procurementRoute: procurementRouteFromOpportunityApi(dto.procurementRoute),
    isFrameContract: dto.isFrameContract ?? false,
    currency: nonEmptyTextOrNull(dto.currency) ?? undefined,
    tcv: dto.isFrameContract ? null : (dto.tcv ?? null),
    tcvLocal: dto.isFrameContract ? null : (dto.tcvLocal ?? null),
    bidBudget: dto.bidBudget ?? null,
    accountPotential: accountPotentialFromOpportunityApi(dto.accountPotential),
    existingRelationship: existingRelationshipFromOpportunityApi(dto.existingRelationship),
    miroWebUrl: nonEmptyTextOrNull(dto.miroWebUrl) ?? undefined,
    sharePointFolderName: nonEmptyTextOrNull(dto.sharePointFolderName) ?? undefined,
    sharePointFolderWebUrl: nonEmptyTextOrNull(dto.sharePointFolderWebUrl) ?? undefined,
    crmOpportunityWebUrl: nonEmptyTextOrNull(dto.crmOpportunityWebUrl) ?? undefined,
    wizardStep: wizardStepFromApiStep(dto.step),
    assessmentDecision: decisionFromApi(
      (dto.assessmentDecisionStatus ?? 'Pending') as OpportunityAssessmentDecisionStatus,
    ),
    isCompleted: dto.isCompleted ?? false,
    completedAt: dto.completedAt ?? undefined,
    workspacePlacement: workspacePlacementFromApi(dto.workspacePlacement),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    stage: salesStageFromApi(dto.salesStage),
  };
}

/** Minimum fields collected in the new-opportunity modal before opening the wizard. */
export interface NewOpportunityBasics {
  title: string;
  accountId: string;
  crmOpportunityId?: string;
  /** Account display name for SharePoint folder preview. */
  accountName: string;
  procurementRoute?: ProcurementRoute;
  confidentiality: ConfidentialityLevel;
}

export function mapNewOpportunityBasicsToCreateWorkspaceBody(
  data: NewOpportunityBasics,
): CreateWorkspaceRequestBody {
  return {
    title: data.title.trim(),
    accountId: data.accountId,
    ...(data.crmOpportunityId ? { crmOpportunityId: data.crmOpportunityId } : {}),
    ...(data.procurementRoute ? { procurementRoute: data.procurementRoute } : {}),
    confidentiality: 'Confidential',
  };
}

export interface OpportunityUpdateRequestBody {
  title?: string;
  accountId?: string;
  customerName?: string;
  dealSize?: string;
  submissionDeadline?: string | null;
  proposalManagerOId?: string | null;
  bidTeamMembers?: OpportunityBidTeamMemberPatchApiDto[];
  procurementRoute?: ProcurementRoute | null;
  briefDescription?: string;
  step?: OpportunityApiStep;
  isFrameContract?: boolean;
  tcv?: number | null;
  tcvLocal?: number | null;
  bidBudget?: number | null;
  accountPotential?: OpportunityApiAccountPotential | null;
  existingRelationship?: OpportunityApiExistingRelationship | null;
}

export interface OpportunityBidTeamMemberPatchApiDto {
  userOId?: string | null;
  roleLabel: string;
}

/** PATCH-style PUT body — only fields that have values in `data`. */
export function mapPartialOpportunityFormDataToUpdateBody(
  data: Partial<OpportunityFormData>,
): OpportunityUpdateRequestBody {
  const brief = data.description?.trim() || undefined;

  const body: OpportunityUpdateRequestBody = {};
  if (data.title !== undefined) body.title = data.title.trim();
  if (data.client !== undefined) body.customerName = data.client.trim();
  if (data.dealSize !== undefined) body.dealSize = mapDealSizeToApi(data.dealSize);
  if (data.submissionDeadline !== undefined) {
    body.submissionDeadline = parseSubmissionDeadlineForApi(data.submissionDeadline) ?? null;
  }
  if (data.proposalManager !== undefined) {
    if (isCatalogUser(data.proposalManager)) {
      body.proposalManagerOId = catalogUserOId(data.proposalManager) ?? undefined;
    } else if (isEmptyMomentumUser(data.proposalManager)) {
      body.proposalManagerOId = ' ';
    }
  }
  if (data.bidTeamMembers !== undefined) {
    const bidTeamMembers = bidTeamMembersToApiPatch(data.bidTeamMembers);
    if (bidTeamMembers !== undefined) {
      body.bidTeamMembers = bidTeamMembers;
    }
  }
  if (data.procurementRoute !== undefined) {
    body.procurementRoute = data.procurementRoute ?? null;
  }
  if (brief !== undefined) body.briefDescription = brief;
  if (data.isFrameContract !== undefined) body.isFrameContract = data.isFrameContract;
  if (data.isFrameContract) {
    body.tcvLocal = null;
  } else if (data.tcvLocal !== undefined) {
    body.tcvLocal = data.tcvLocal;
  }
  if (data.bidBudget !== undefined) body.bidBudget = data.bidBudget;
  if (data.accountPotential !== undefined) {
    body.accountPotential = accountPotentialToOpportunityApi(data.accountPotential) ?? null;
  }
  if (data.existingRelationship !== undefined) {
    body.existingRelationship =
      existingRelationshipToOpportunityApi(data.existingRelationship) ?? null;
  }
  return body;
}
